import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Users, 
  UserPlus, 
  GraduationCap,
  Link2,
  Calendar,
  MessageSquare,
  FileText,
  Clock,
  AlertCircle,
  ArrowRight,
  BookOpen,
  UserCheck
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL || '';

function SecretaryDashboard({ user }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_students: 0,
    total_parents: 0,
    recent_absences: 0,
    unlinked_students: 0
  });
  const [recentAbsences, setRecentAbsences] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);

  useEffect(() => {
    fetchSecretaryData();
  }, []);

  const fetchSecretaryData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch students
      const studentsResponse = await axios.get(`${API}/api/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch parents
      const parentsResponse = await axios.get(`${API}/api/users?role=PARENT`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch recent absences
      const absencesResponse = await axios.get(`${API}/api/attendance?status=ABSENT&limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch recent messages
      const messagesResponse = await axios.get(`${API}/api/messages?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const students = studentsResponse.data;
      const parents = parentsResponse.data;
      const unlinked = students.filter(s => !s.parent_ids || s.parent_ids.length === 0);
      
      setStats({
        total_students: students.length,
        total_parents: parents.length,
        recent_absences: absencesResponse.data.length,
        unlinked_students: unlinked.length
      });
      
      setRecentAbsences(absencesResponse.data);
      setRecentMessages(messagesResponse.data);
      
    } catch (error) {
      console.error('Error fetching secretary data:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les données',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Bonjour, {user.first_name} !</h1>
          <p className="text-gray-600 mt-1">Tableau de bord Secrétaire</p>
        </div>
        <div className="flex items-center space-x-2">
          <FileText className="w-8 h-8 text-purple-600" />
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Students */}
        <Card className="card hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/students')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Élèves</CardTitle>
            <GraduationCap className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {stats.total_students}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Élèves inscrits
            </p>
          </CardContent>
        </Card>

        {/* Total Parents */}
        <Card className="card hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/parent-links')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Parents</CardTitle>
            <Users className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {stats.total_parents}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Parents enregistrés
            </p>
          </CardContent>
        </Card>

        {/* Recent Absences */}
        <Card className="card hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/attendance')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Absences Récentes</CardTitle>
            <Calendar className="h-5 w-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {stats.recent_absences}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Dernières absences
            </p>
          </CardContent>
        </Card>

        {/* Unlinked Students */}
        <Card className="card hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/parent-links')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sans Parent Lié</CardTitle>
            <AlertCircle className="h-5 w-5 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {stats.unlinked_students}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Élèves à lier
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="card">
        <CardHeader>
          <CardTitle>Actions Rapides</CardTitle>
          <CardDescription>Accès rapide aux fonctionnalités principales</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Button 
              className="h-24 flex flex-col items-center justify-center space-y-2"
              onClick={() => navigate('/dashboard/create-users')}
            >
              <UserPlus className="w-6 h-6" />
              <span className="text-xs">Créer Élève</span>
            </Button>
            
            <Button 
              className="h-24 flex flex-col items-center justify-center space-y-2"
              variant="outline"
              onClick={() => navigate('/dashboard/create-users')}
            >
              <Users className="w-6 h-6" />
              <span className="text-xs">Créer Parent</span>
            </Button>
            
            <Button 
              className="h-24 flex flex-col items-center justify-center space-y-2"
              variant="outline"
              onClick={() => navigate('/dashboard/student-parent')}
            >
              <Link2 className="w-6 h-6" />
              <span className="text-xs">Lier Parent-Enfant</span>
            </Button>
            
            <Button 
              className="h-24 flex flex-col items-center justify-center space-y-2"
              variant="outline"
              onClick={() => navigate('/dashboard/attendance')}
            >
              <UserCheck className="w-6 h-6" />
              <span className="text-xs">Absences</span>
            </Button>
            
            <Button 
              className="h-24 flex flex-col items-center justify-center space-y-2"
              variant="outline"
              onClick={() => navigate('/dashboard/grades')}
            >
              <BookOpen className="w-6 h-6" />
              <span className="text-xs">Notes</span>
            </Button>
            
            <Button 
              className="h-24 flex flex-col items-center justify-center space-y-2"
              variant="outline"
              onClick={() => navigate('/dashboard/messages')}
            >
              <MessageSquare className="w-6 h-6" />
              <span className="text-xs">Messages</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Absences */}
        <Card className="card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Absences Récentes</CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/dashboard/attendance')}
              >
                Voir tout <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentAbsences.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Aucune absence récente
                </p>
              ) : (
                recentAbsences.map((absence) => (
                  <div 
                    key={absence.id} 
                    className="flex items-center justify-between p-3 bg-orange-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <AlertCircle className="w-5 h-5 text-orange-600" />
                      <div>
                        <p className="font-medium text-sm">
                          {absence.student_name || 'Élève'}
                        </p>
                        <p className="text-xs text-gray-600">
                          {absence.class_name || 'Classe'} - {absence.subject || 'Toutes matières'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {absence.date ? new Date(absence.date).toLocaleDateString('fr-FR') : '-'}
                        </p>
                      </div>
                    </div>
                    <Badge className="bg-orange-100 text-orange-800 text-xs">
                      {absence.status === 'ABSENT' ? 'Absent' : 'Retard'}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Messages */}
        <Card className="card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Messages Récents</CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/dashboard/messages')}
              >
                Voir tout <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentMessages.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Aucun message récent
                </p>
              ) : (
                recentMessages.map((message) => (
                  <div 
                    key={message.id} 
                    className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => navigate('/dashboard/messages')}
                  >
                    <MessageSquare className="w-5 h-5 text-blue-600 mt-1" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm truncate">
                          {message.sender_name || 'Expéditeur'}
                        </p>
                        {!message.read && (
                          <Badge className="bg-blue-100 text-blue-800 text-xs ml-2">
                            Nouveau
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 truncate">
                        {message.subject || 'Sans objet'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {message.created_at ? new Date(message.created_at).toLocaleDateString('fr-FR') : '-'}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Important Tasks */}
      {stats.unlinked_students > 0 && (
        <Card className="card bg-red-50 border-red-200">
          <CardHeader>
            <CardTitle className="text-red-900 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              Attention
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-800">
              Vous avez <strong>{stats.unlinked_students} élève(s)</strong> sans parent lié. 
              Pensez à créer les liens parent-enfant pour permettre aux parents d'accéder aux informations de leurs enfants.
            </p>
            <Button 
              className="mt-3" 
              size="sm"
              onClick={() => navigate('/dashboard/parent-links')}
            >
              Gérer les liens <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="card bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-900">💼 Rôle Secrétaire</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-blue-800">
            En tant que secrétaire, vous avez accès à la gestion complète des élèves, parents, absences et notes. 
            Vous jouez un rôle clé dans le bon fonctionnement administratif de l'établissement.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default SecretaryDashboard;
